import React, { useMemo, useState } from 'react';
import {
  Badge, Box, IconButton, InputAdornment, List, ListItemAvatar, ListItemButton, ListItemText, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { useNavigate } from 'react-router-dom';
import BotAvatar from './BotAvatar';
import StateBoundary from './StateBoundary';
import { timeAgo } from '../utils/format';

/** Left side of the chat screen: searchable list of the team's chats with unread markers. */
export default function ConversationList({
  items, loading, error, onRetry, selectedId, onSelect, canAdd,
}) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((c) => `${c.title} ${c.bot?.role || ''}`.toLowerCase().includes(q)) : items;
  }, [items, query]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, borderRight: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search chats"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            sx: { borderRadius: 5, bgcolor: 'action.hover' },
          }}
          inputProps={{ 'aria-label': 'Search chats' }}
        />
        {canAdd && (
          <Tooltip title="Add a team member">
            <IconButton aria-label="Add a team member" onClick={() => navigate('/team?new=1')}><PersonAddAltOutlinedIcon /></IconButton>
          </Tooltip>
        )}
      </Stack>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 0 }}>
        <Box sx={{ px: 1.5 }}>
          <StateBoundary loading={loading} error={error} onRetry={onRetry} rows={6}>
            {shown.length === 0 && (
              <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                {query ? 'No chats match your search.' : 'No chats yet.'}
              </Typography>
            )}
          </StateBoundary>
        </Box>
        <List disablePadding>
          {!loading && !error && shown.map((c) => (
            <ListItemButton key={c.id} selected={c.id === selectedId} onClick={() => onSelect(c.id)} sx={{ py: 1.25, px: 1.5, alignItems: 'flex-start' }}>
              <ListItemAvatar sx={{ minWidth: 52 }}><BotAvatar bot={c.bot} group={c.type === 'group'} size={42} /></ListItemAvatar>
              <ListItemText
                primary={(
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Typography noWrap fontWeight={c.unread ? 700 : 600}>{c.title}</Typography>
                    <Typography variant="caption" color={c.unread ? 'primary' : 'text.secondary'} sx={{ flexShrink: 0 }}>
                      {c.last_message ? timeAgo(c.last_message.at) : ''}
                    </Typography>
                  </Stack>
                )}
                secondary={(
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ fontWeight: c.unread ? 600 : 400 }}>
                      {c.last_message
                        ? `${c.last_message.sender && c.type === 'group' ? `${c.last_message.sender}: ` : ''}${c.last_message.kind === 'approval_card' ? 'Waiting for your approval' : c.last_message.preview}`
                        : (c.bot?.role || 'No messages yet')}
                    </Typography>
                    {c.unread > 0 && <Badge color="primary" badgeContent={c.unread} sx={{ mr: 1 }} />}
                  </Stack>
                )}
                secondaryTypographyProps={{ component: 'div' }}
                primaryTypographyProps={{ component: 'div' }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Box>
  );
}
